import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PolicyPageLayout from './PolicyPageLayout';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "Information We Collect",
      content: [
        "We collect information you provide directly to us when you create an account, use our services, or communicate with us. This includes your name, email address, payment information, and audio files you process through our platform."
      ],
      bullets: [
        "Account information: name, email, password, and profile details",
        "Payment data: billing address and payment method information",
        "Audio content: files you upload for processing and analysis",
        "Usage data: how you interact with our services and features",
        "Device information: IP address, browser type, and operating system"
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "We use the information we collect to provide, maintain, and improve our AI audio processing services. Your data helps us deliver personalized experiences and develop new features."
      ],
      bullets: [
        "Process and analyze your audio files using our AI algorithms",
        "Provide customer support and respond to your inquiries",
        "Send you technical notices and service updates",
        "Detect and prevent fraud, abuse, and security issues",
        "Comply with legal obligations and enforce our terms"
      ]
    },
    {
      title: "Data Sharing and Disclosure",
      content: [
        "We do not sell your personal information. We may share your data with third-party service providers who assist us in operating our platform, subject to strict confidentiality obligations."
      ],
      bullets: [
        "Cloud infrastructure providers for data storage and processing",
        "Payment processors for transaction handling",
        "Analytics services for service improvement",
        "Legal authorities when required by law"
      ]
    },
    {
      title: "Data Security",
      content: [
        "We implement industry-standard security measures to protect your information. All audio files are encrypted using AES-256 encryption both in transit and at rest. We regularly audit our security practices and maintain SOC 2 compliance."
      ]
    },
    {
      title: "Your Rights and Choices",
      content: [
        "You have the right to access, correct, or delete your personal information. You can also opt out of certain data processing activities and request a copy of your data in a portable format."
      ],
      bullets: [
        "Access and download your personal data",
        "Request correction of inaccurate information",
        "Delete your account and associated data",
        "Opt out of marketing communications",
        "Object to certain data processing activities"
      ]
    }
  ];

  const faqs = [
    {
      question: "How long do you retain my audio files?",
      answer: "We retain processed audio files for 30 days after processing to allow you to download them. After this period, files are automatically deleted unless you choose to store them in your account library."
    },
    {
      question: "Can I request deletion of all my data?",
      answer: "Yes, you can request complete deletion of your account and all associated data. This process takes up to 30 days to complete and is irreversible."
    },
    {
      question: "Do you use my audio for AI training?",
      answer: "No, we do not use your audio files to train our AI models without explicit consent. Your content remains private and is only used to provide you with our services."
    },
    {
      question: "How do you handle data breaches?",
      answer: "In the event of a data breach, we will notify affected users within 72 hours and take immediate steps to secure systems. We maintain comprehensive incident response procedures."
    }
  ];

  const navigate = useNavigate();

  return (
    <>
      {/* Back to Home Button */}
      <div className="fixed top-4 left-4 z-50">
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-orange/10 border border-neon-orange/30 text-neon-orange hover:bg-neon-orange/20 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Back to home"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm font-medium">Home</span>
        </motion.button>
      </div>

      <PolicyPageLayout
        title="Privacy Policy"
        lastUpdated="March 1, 2026"
        sections={sections}
        faqs={faqs}
      />
    </>
  );
};

export default PrivacyPolicy;
